import { useTu } from "@src/appV2/i18n/useTu";
import { Box, Link, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { CreateOrResetCredentialsModal } from "@src/appV2/WorkerCredentials/CreateOrResetCredentialsModal";
import { ManageCredentialsModal } from "@src/appV2/WorkerCredentials/ManageCredentialsModal";
import { useModalState } from "@src/lib/ui-react";
import { ChartingCredentialSoftware } from "../../../WorkerCredentials/api/schema";
import { useChartingCredentialProvisioningConfig } from "../../../WorkerCredentials/api/useCredentialProvisioningConfig";
import { Text } from "../../components/Text";
import { parseWorkerCredentialMessage } from "../utils/parseWorkerCredentialMessage";

interface WorkerCredentialRequestMessageProps {
  message: string;
  workerName: string;
  workerId: string;
  workplaceId: string;
  sx?: SxProps<Theme>;
}

/**
 * Renders a worker credential request message with a link to manage credentials.
 * Shows different text based on whether worker has existing credentials or new ones were created.
 * Only renders when charting credential integration is enabled.
 */
export function WorkerCredentialRequestMessage(props: WorkerCredentialRequestMessageProps) {
  const { tu } = useTu("chat");

  const { message, workerName, workerId, workplaceId, sx } = props;
  const chartingCredentialIntegrationConfig = useChartingCredentialProvisioningConfig();
  const manageCredentialsModalState = useModalState();
  const createOrResetCredentialsModalState = useModalState();

  if (!chartingCredentialIntegrationConfig.enabled) {
    return null;
  }

  const software = ChartingCredentialSoftware.POINT_CLICK_CARE;
  const { hasExistingCredentials } = parseWorkerCredentialMessage(message);

  const messageText = hasExistingCredentials
    ? `${workerName}'s ${software} account was reset.`
    : `A new ${software} account has been created for ${workerName}.`;

  const handleViewDetails = () => {
    manageCredentialsModalState.openModal();
  };

  const handleAction = () => {
    manageCredentialsModalState.closeModal();
    createOrResetCredentialsModalState.openModal();
  };

  const handleActionComplete = () => {
    createOrResetCredentialsModalState.closeModal();
    manageCredentialsModalState.openModal();
  };

  return (
    <>
      <Box
        sx={mergeSxProps(
          {
            display: "flex",
            justifyContent: "center",
            padding: 2,
          },
          sx
        )}
      >
        <Text
          variant="caption"
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            textAlign: "center",
          })}
        >
          {messageText}{" "}
          <Link
            component="button"
            sx={(theme) => ({
              color: theme.palette.primary.main,
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "inherit",
              fontFamily: "inherit",
              verticalAlign: "baseline",
            })}
            onClick={handleViewDetails}
          >
            {tu("View details")}
          </Link>
        </Text>
      </Box>
      {manageCredentialsModalState.modalIsOpen && (
        <ManageCredentialsModal
          modalState={manageCredentialsModalState}
          workerId={workerId}
          workerName={workerName}
          workplaceId={workplaceId}
          onAction={handleAction}
        />
      )}
      {createOrResetCredentialsModalState.modalIsOpen && (
        <CreateOrResetCredentialsModal
          modalState={createOrResetCredentialsModalState}
          workerId={workerId}
          workerName={workerName}
          workplaceId={workplaceId}
          onAction={handleActionComplete}
        />
      )}
    </>
  );
}
